import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import '../styles/register.css';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organizationName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'manager',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      await register({
        ...userData,
        role: 'manager',
        organizationName: formData.organizationName,
      });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="register-page"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#ffffff',
        py: { xs: 4, md: 8 },
        px: { xs: 0, md: 4 },
      }}
    >
      <Container className="register-container" maxWidth={false} sx={{ px: { xs: 2, md: 6 } }}>
        <Paper
          className="register-container"
          elevation={3}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.1fr 1.1fr' },
            minHeight: { md: 520 },
            width: '100%',
            maxWidth: 1180,
            mx: 'auto',
          }}
        >
          {/* Left: copy and form */}
          <Box
            className="register-page__inner"
            sx={{
              px: { xs: 3, md: 6 },
              py: { xs: 4, md: 6 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              bgcolor: '#ffffff',
            }}
          >
            <Box className="logo-register"
              sx={{ mb: 3, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/sglogo2.svg`}
                alt="SuperGauge"
                sx={{ height: 24, display: 'block' }}
              />
            </Box>
            <Typography className="register-title" variant="h4" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
              Great workplaces start with good conversations
            </Typography>
            <Typography
              className="register-subtitle"
              variant="body1"
              gutterBottom
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 420 }}
            >
              A couple of quick details and you'll be ready to go. No credit card required.
            </Typography>

            {error && (
              <Alert className="register-error" severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box className="register-form" component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1.4fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    className="register-field"
                    fullWidth
                    label="First name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    margin="normal"
                    required
                    autoFocus
                  />
                  <TextField
                    className="register-field"
                    fullWidth
                    label="Last name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Box>
              </Box>
              <TextField
                className="register-field"
                fullWidth
                label="Organisation name"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleChange}
                margin="normal"
                required
              />
              <TextField
                className="register-field"
                fullWidth
                label="Work email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
                autoComplete="email"
              />
              <TextField
                className="register-field"
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                autoComplete="new-password"
              />
              <TextField
                className="register-field"
                fullWidth
                label="Confirm password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                margin="normal"
                required
                autoComplete="new-password"
              />

              <Button
                className="register-submit"
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 1.5 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Create account'}
              </Button>
              <Box className="register-login-link" sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{' '}
                  <Link component={RouterLink} to="/login" variant="body2">
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right: placeholder image panel */}
          <Box
            className="register-visual"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'relative',
              bgcolor: '#001b3a',
              backgroundImage: `url(${process.env.PUBLIC_URL}/registerbg.webp)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;
